import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from "recharts";
import { AlertCircle, TrendingUp, TrendingDown } from "lucide-react";

function formatMaaned(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString("da-DK", { month: "short", year: "2-digit" });
}

export default function KlientvaekstScenariePanelIV() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch("/api/os/vaekst/klientvaekst-scenarie", { credentials: "include" })
      .then(r => r.ok ? r.json() : Promise.reject(r.statusText))
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(String(e)); setLoading(false); });
  }, []);

  if (loading) return <div style={{ color: "#9ca3af", padding: 24 }}>Henter klientvaekst...</div>;
  if (error) return <div style={{ color: "#ef4444", padding: 24, display: "flex", alignItems: "center", gap: 8 }}><AlertCircle size={16} /> {error}</div>;

  const { historik, scenarier, forskel_vs_realistisk } = data;

  // Saml alle data-punkter til et dataset
  const scenarieMap = {};
  (scenarier || []).forEach(s => { scenarieMap[s.maaned] = s; });

  const chartData = (historik || []).map(h => {
    const md = new Date(h.maaned).toISOString().slice(0, 7);
    const sc = scenarieMap[md] || {};
    return {
      maaned: formatMaaned(h.maaned),
      faktisk: h.kumulativ,
      konservativt: sc.konservativt,
      realistisk: sc.realistisk,
      ambitiost: sc.ambitiost,
    };
  });

  // Tilfoej fremtidige scenarier uden historisk data
  const eksisterendeMdr = new Set((historik || []).map(h => new Date(h.maaned).toISOString().slice(0, 7)));
  (scenarier || []).forEach(s => {
    if (!eksisterendeMdr.has(s.maaned)) {
      chartData.push({
        maaned: formatMaaned(s.maaned + "-01"),
        faktisk: null,
        konservativt: s.konservativt,
        realistisk: s.realistisk,
        ambitiost: s.ambitiost,
      });
    }
  });

  const foran = forskel_vs_realistisk >= 0;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        <div style={{ flex: 1, minWidth: 200, background: foran ? "rgba(34,197,94,0.1)" : "rgba(239,68,68,0.1)", border: `1px solid ${foran ? "#22c55e" : "#ef4444"}`, borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
          {foran ? <TrendingUp size={18} color="#22c55e" /> : <TrendingDown size={18} color="#ef4444" />}
          <div>
            <div style={{ color: foran ? "#22c55e" : "#ef4444", fontWeight: 700, fontSize: 16 }}>
              {Math.abs(forskel_vs_realistisk || 0)} klienter {foran ? "foran" : "bagud"}
            </div>
            <div style={{ color: "#9ca3af", fontSize: 12 }}>vs. realistisk scenarie</div>
          </div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={280}>
        <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
          <XAxis dataKey="maaned" tick={{ fill: "#9ca3af", fontSize: 11 }} />
          <YAxis tick={{ fill: "#9ca3af", fontSize: 11 }} />
          <Tooltip contentStyle={{ background: "#1e2235", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
          <Legend wrapperStyle={{ color: "#9ca3af", fontSize: 12 }} />
          <Line type="monotone" dataKey="faktisk" name="Faktisk" stroke="#3b82f6" strokeWidth={2.5} dot={false} connectNulls={false} />
          <Line type="monotone" dataKey="realistisk" name="Realistisk" stroke="#22c55e" strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
          <Line type="monotone" dataKey="ambitiost" name="Ambitiost" stroke="#a855f7" strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
          <Line type="monotone" dataKey="konservativt" name="Konservativt" stroke="#6b7280" strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
